import { Box, Button, Dialog, DialogTitle, DialogContent } from '@mui/material';

import { useTranslate } from 'src/locales';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';

import { Form } from 'src/components/hook-form';
import { FieldsRenderer } from 'src/components/field-renderer/field-renderer';

import { useAccountForm } from '../hooks/use-account-form';
import { accountFormConfig } from '../config/account-form.config';

import type { IAccount } from '../accounts.types';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: IAccount;
  handleSubmitData: (data: any, assignedPayload: any) => void;
  isLoader?: boolean;
  customFields: any[] | [];
};

export function AccountAddEditForm({
  open,
  onClose,
  selectedUser,
  handleSubmitData,
  isLoader = false,
  customFields,
}: Props) {
  const { t } = useTranslate('translation');

  const { formKey, methods, onSubmit, assigneeListProps } = useAccountForm({
    open,
    selectedUser,
    handleSubmitData,
    customFields,
  });

  const title = `${selectedUser && selectedUser?.name !== '' ? t('EDIT') : t('CREATE')} ${t('ACCOUNT')}`;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiPaper-rounded': {
          p: 0.5,
          boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <DialogTitle sx={[(theme) => ({ p: theme.spacing(3, 3, 2, 3) })]}>{title}</DialogTitle>

      <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
        {isLoader && <AuthLoader />}
        <Form key={formKey} methods={methods} onSubmit={onSubmit}>
          {/* Render fields from JSON config */}
          {accountFormConfig.map((section, index) => (
            <Box key={index} sx={{ mb: index === accountFormConfig.length - 1 ? 0 : 3 }}>
              <FieldsRenderer
                fields={section.fields.map((field) => ({
                  ...field,
                  // Pass additional props for multiAssign field
                  componentProps:
                    field.name === 'assignUsers'
                      ? {
                          ...field.componentProps,
                          ...assigneeListProps,
                        }
                      : field.componentProps,
                }))}
                control={methods.control}
                useGrid
              />
            </Box>
          ))}

          {/* Custom fields */}
          {customFields && customFields.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <FieldsRenderer
                fields={customFields.map((field) => ({
                  name: field.name || `cf_${field.id}`,
                  label: field.label || field.name || '',
                  type: 'customField' as const,
                  gridSize: { xs: 12, md: 12 },
                  componentProps: {
                    fieldData: field,
                  },
                }))}
                control={methods.control}
                useGrid
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, py: 3 }}>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {t('CANCEL')}
            </Button>
            <Button type="submit" variant="contained" disabled={methods.formState.isSubmitting}>
              {t('SAVE')}
            </Button>
          </Box>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
